import type { ReactElement } from 'react';

/**
 * K3c: why_us's built-in fallback for the hero panel when there is no
 * `content.imageUrl` yet. The v2-derived version rendered an empty flat
 * rectangle (`.mv3-image-empty-dark`) there, which reads as an unfinished
 * slide on a client-facing deck rather than "no photo yet, by design."
 *
 * A themed brand-palette treatment instead: soft rings and a glow built from
 * the same `--mv3-mint` / `--mv3-lime` / `--mv3-dark-bg*` tokens every other
 * v3 slide uses (momentum-v3.css maps these to `--doc-primary/accent/dark-*`,
 * which `web/src/lib/theme.ts`'s `themeStyle()` derives from the proposal's
 * OWN extracted brand color via `src/domain/brand-palette.ts` when one is
 * active — a proposal with no usable brand color falls back to the family's
 * default mint/lime, so this always matches the rest of THIS deck). Shapes
 * only: no invented numbers, no stock-photo stand-in, no text — consistent
 * with the global "no placeholder card" rule (momentum-v3-slides.md rule 6).
 * Stream K4 fills this slot with a real Pexels photo when the agent finds
 * one; this is only what renders before that, or if it never does.
 */
export function WhyUsArt(): ReactElement {
  return (
    <svg
      className="mv3-whyus-art"
      viewBox="0 0 480 600"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="mv3-whyus-art-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--mv3-dark-bg-2)" />
          <stop offset="100%" stopColor="var(--mv3-dark-bg)" />
        </linearGradient>
        <radialGradient id="mv3-whyus-art-glow" cx="70%" cy="20%" r="60%">
          <stop offset="0%" stopColor="var(--mv3-mint)" stopOpacity="0.32" />
          <stop offset="100%" stopColor="var(--mv3-mint)" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="mv3-whyus-art-glow-2" cx="18%" cy="82%" r="45%">
          <stop offset="0%" stopColor="var(--mv3-lime)" stopOpacity="0.16" />
          <stop offset="100%" stopColor="var(--mv3-lime)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="480" height="600" fill="url(#mv3-whyus-art-bg)" />
      <rect width="480" height="600" fill="url(#mv3-whyus-art-glow)" />
      <rect width="480" height="600" fill="url(#mv3-whyus-art-glow-2)" />
      <circle cx="352" cy="150" r="120" fill="none" stroke="var(--mv3-lime)" strokeOpacity="0.45" strokeWidth="2" />
      <circle cx="352" cy="150" r="172" fill="none" stroke="var(--mv3-mint)" strokeOpacity="0.28" strokeWidth="1.5" />
      <circle cx="352" cy="150" r="224" fill="none" stroke="var(--mv3-mint)" strokeOpacity="0.14" strokeWidth="1.5" />
      <circle cx="96" cy="486" r="86" fill="var(--mv3-mint)" fillOpacity="0.14" />
      <line x1="40" y1="600" x2="252" y2="352" stroke="var(--mv3-lime)" strokeOpacity="0.3" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
